import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EditorialLayout from '@/components/editorial/EditorialLayout';
import TemplateEditor from '@/components/email/TemplateEditor';
import TemplatePreview from '@/components/email/TemplatePreview';
import { useEmailTemplates } from '@/hooks/email/useEmailTemplates';
import { Loader2, Star, Mail } from 'lucide-react';

const EmailTemplatePage: React.FC = () => {
  const {
    templates,
    isLoading,
    isSaving,
    error,
    updateTemplate,
    setDefaultTemplate
  } = useEmailTemplates();

  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);

  // Set initial active template when templates are loaded
  React.useEffect(() => {
    if (templates.length > 0 && !activeTemplateId) {
      // Default to the default template or first one
      const defaultTemplate = templates.find(t => t.is_default) || templates[0];
      setActiveTemplateId(defaultTemplate.id);
    }
  }, [templates, activeTemplateId]);

  const activeTemplate = templates.find(t => t.id === activeTemplateId);

  if (isLoading) {
    return (
      <EditorialLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </EditorialLayout>
    );
  }

  if (error) {
    return (
      <EditorialLayout>
        <div className="max-w-7xl mx-auto py-8 px-4">
          <div className="text-center text-destructive">
            <p>{error}</p>
          </div>
        </div>
      </EditorialLayout>
    );
  }

  return (
    <EditorialLayout>
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">E-postmaler</h1>
          </div>
          <p className="text-muted-foreground">
            Administrer 5 maler for intervjuforespørsler. Hver mal kan tilpasses med egen tekst og avsenderinformasjon.
          </p>
        </div>
        
        <Tabs value={activeTemplateId || ''} onValueChange={setActiveTemplateId}>
          <TabsList className="mb-6 flex-wrap h-auto gap-2">
            {templates.map((template) => (
              <TabsTrigger 
                key={template.id} 
                value={template.id}
                className="flex items-center gap-2"
              >
                {template.is_default && <Star className="h-3 w-3 fill-current" />}
                {template.name}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {templates.map((template) => (
            <TabsContent key={template.id} value={template.id}>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Editor */}
                <div>
                  <TemplateEditor
                    template={template}
                    onSave={updateTemplate}
                    onSetDefault={setDefaultTemplate}
                    isSaving={isSaving}
                  />
                </div>
                
                {/* Preview */}
                <div className="xl:sticky xl:top-4 xl:self-start">
                  <TemplatePreview template={template} />
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </EditorialLayout>
  );
};

export default EmailTemplatePage;
